import { lazy } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";

import { routers } from "@/configs";
import OriginalMeta from "@/pages/Casino/OriginalMeta";

const Games = lazy(() => import("@/pages/Casino/Games"));
const GameRoom = lazy(() => import("@/pages/Casino/GameRoom"));
const GamePage = lazy(() => import("@/pages/Casino/GamePage"));
const Providers = lazy(() => import("@/pages/Casino/Providers"));
const ProviderPage = lazy(() => import("@/pages/Casino/ProviderPage"));
const Crash = lazy(() => import("@/pages/Casino/Crash"));
const Limbo = lazy(() => import("@/pages/Casino/Limbo"));
const Plinko = lazy(() => import("@/pages/Casino/Plinko"));
const Keno = lazy(() => import("@/pages/Casino/Keno"));
const Diamonds = lazy(() => import("@/pages/Casino/Diamonds"));
const Coinflip = lazy(() => import("@/pages/Casino/Coinflip"));
const Mines = lazy(() => import("@/pages/Casino/Mines"));
const Slide = lazy(() => import("@/pages/Casino/Slide"));
const FlowerPoker = lazy(() => import("@/pages/Casino/FlowerPoker"));
const Blackjack = lazy(() => import("@/pages/Casino/Blackjack"));
const Wheel = lazy(() => import("@/pages/Casino/Wheel"));
const Goal = lazy(() => import("@/pages/Casino/Goal"));
const Snakes = lazy(() => import("@/pages/Casino/Snakes"));
const Chicken = lazy(() => import("@/pages/Casino/Chicken"));
const Roulette = lazy(() => import("@/pages/Casino/Roulette"));
const VideoPoker = lazy(() => import("@/pages/Casino/VideoPoker"));
const Dice = lazy(() => import("@/pages/Casino/Dice"));
const Darts = lazy(() => import("@/pages/Casino/Darts"));
const Baccarat = lazy(() => import("@/pages/Casino/Baccarat"));
const BaccaratTable = lazy(() => import("@/pages/Casino/BaccaratTable"));
const Hilo = lazy(() => import("@/pages/Casino/Hilo"));
const HiloTable = lazy(() => import("@/pages/Casino/HiloTable"));
const Poker = lazy(() => import("@/pages/Casino/Poker"));
const Rummy = lazy(() => import("@/pages/Casino/Rummy"));
const Truco = lazy(() => import("@/pages/Casino/Truco"));
const Chinchon = lazy(() => import("@/pages/Casino/Chinchon"));
const TeenPatti = lazy(() => import("@/pages/Casino/TeenPatti"));
const RockPaperScissors = lazy(() => import("@/pages/Casino/RockPaperScissors"));
const RussianRoulette = lazy(() => import("@/pages/Casino/RussianRoulette"));
const Search = lazy(() => import("@/pages/Casino/Search"));

// The query goes along, so "/?lang=es" (sitemap, ads) opens the Spanish lobby.
const ToLobby = () => {
  const { search } = useLocation();
  return (
    <Navigate to={{ pathname: routers.CasinoGame.replace(":code", "lobby"), search }} replace />
  );
};

// In-house games, each with its display name for the page head.
const ORIGINALS = [
  { path: routers.Crash, name: "Crash", Page: Crash },
  { path: routers.Limbo, name: "Limbo", Page: Limbo },
  { path: routers.Plinko, name: "Plinko", Page: Plinko },
  { path: routers.Keno, name: "Keno", Page: Keno },
  { path: routers.Diamonds, name: "Diamonds", Page: Diamonds },
  { path: routers.Coinflip, name: "Coin Flip", Page: Coinflip },
  { path: routers.Mines, name: "Mines", Page: Mines },
  { path: routers.Slide, name: "Slide", Page: Slide },
  { path: routers.FlowerPoker, name: "Flower Poker", Page: FlowerPoker },
  { path: routers.Blackjack, name: "Blackjack", Page: Blackjack },
  { path: routers.Wheel, name: "Wheel", Page: Wheel },
  { path: routers.Goal, name: "Goal", Page: Goal },
  { path: routers.Snakes, name: "Snakes", Page: Snakes },
  { path: routers.Chicken, name: "Chicken", Page: Chicken },
  { path: routers.Roulette, name: "Roulette", Page: Roulette },
  { path: routers.VideoPoker, name: "Video Poker", Page: VideoPoker },
  { path: routers.Dice, name: "Dice", Page: Dice },
  { path: routers.Darts, name: "Darts", Page: Darts },
  { path: routers.Baccarat, name: "Baccarat", Page: Baccarat },
  { path: routers.BaccaratTable, name: "Baccarat Table", Page: BaccaratTable },
  { path: routers.Hilo, name: "Hi-Lo", Page: Hilo },
  { path: routers.HiloTable, name: "Hi-Lo Table", Page: HiloTable },
  // The lobby and a table are one page; the table id comes from the route.
  { path: routers.Poker, name: "Poker", Page: Poker },
  { path: routers.PokerTable, name: "Poker", Page: Poker },
  { path: routers.Omaha, name: "Omaha Poker", Page: Poker },
  { path: routers.OmahaTable, name: "Omaha Poker", Page: Poker },
  { path: routers.Rummy, name: "Rummy", Page: Rummy },
  { path: routers.RummyTable, name: "Rummy", Page: Rummy },
  { path: routers.Truco, name: "Truco", Page: Truco },
  { path: routers.TrucoTable, name: "Truco", Page: Truco },
  { path: routers.Chinchon, name: "Chinchon", Page: Chinchon },
  { path: routers.ChinchonTable, name: "Chinchon", Page: Chinchon },
  { path: routers.TeenPatti, name: "Teen Patti", Page: TeenPatti },
  { path: routers.TeenPattiTable, name: "Teen Patti", Page: TeenPatti },
  { path: routers.RockPaperScissors, name: "Rock Paper Scissors", Page: RockPaperScissors },
  { path: routers.RussianRoulette, name: "Russian Roulette", Page: RussianRoulette },
];

// Old in-house game URLs: bookmarks, table invite links and admin-set links still point here.
const LEGACY_ORIGINALS = [
  { path: "/casino/crash-original-game", to: routers.Crash },
  { path: "/casino/limbo-original-game", to: routers.Limbo },
  { path: "/casino/plinko-original-game", to: routers.Plinko },
  { path: "/casino/keno", to: routers.Keno },
  { path: "/casino/diamonds-original-game", to: routers.Diamonds },
  { path: "/casino/coinflip-original-game", to: routers.Coinflip },
  { path: "/casino/mines-original-game", to: routers.Mines },
  { path: "/casino/slide-original-game", to: routers.Slide },
  { path: "/casino/flower-poker-original-game", to: routers.FlowerPoker },
  { path: "/casino/blackjack-original-game", to: routers.Blackjack },
  { path: "/casino/wheel-original-game", to: routers.Wheel },
  { path: "/casino/goal-original-game", to: routers.Goal },
  { path: "/casino/snakes-original-game", to: routers.Snakes },
  { path: "/casino/chicken-original-game", to: routers.Chicken },
  { path: "/casino/roulette-original-game", to: routers.Roulette },
  { path: "/casino/video-poker-original-game", to: routers.VideoPoker },
  { path: "/casino/dice-original-game", to: routers.Dice },
  { path: "/casino/darts-original-game", to: routers.Darts },
  { path: "/casino/baccarat-original-game", to: routers.Baccarat },
  { path: "/casino/baccarat-table-original-game", to: routers.BaccaratTable },
  { path: "/casino/hilo-original-game", to: routers.Hilo },
  { path: "/casino/hilo-table-original-game", to: routers.HiloTable },
  { path: "/casino/poker-original-game", to: routers.Poker },
  { path: "/casino/poker-original-game/:tableId", to: routers.PokerTable },
  { path: "/casino/omaha-original-game", to: routers.Omaha },
  { path: "/casino/omaha-original-game/:tableId", to: routers.OmahaTable },
  { path: "/casino/rummy-original-game", to: routers.Rummy },
  { path: "/casino/rummy-original-game/:tableId", to: routers.RummyTable },
  { path: "/casino/truco-original-game", to: routers.Truco },
  { path: "/casino/truco-original-game/:tableId", to: routers.TrucoTable },
  { path: "/casino/chinchon-original-game", to: routers.Chinchon },
  { path: "/casino/chinchon-original-game/:tableId", to: routers.ChinchonTable },
  { path: "/casino/teen-patti-original-game", to: routers.TeenPatti },
  { path: "/casino/teen-patti-original-game/:tableId", to: routers.TeenPattiTable },
  { path: "/casino/rock-paper-scissors-original-game", to: routers.RockPaperScissors },
  { path: "/casino/russian-roulette-original-game", to: routers.RussianRoulette },
];

// The query goes along: a private table's invite code is in it.
const ToOriginal = ({ to }: { to: string }) => {
  const { search, hash } = useLocation();
  const { tableId = "" } = useParams();
  return <Navigate to={{ pathname: to.replace(":tableId", tableId), search, hash }} replace />;
};

export const CasinoRoutes = [
  {
    path: routers.Home,
    element: <ToLobby />,
  },
  {
    path: routers.Search,
    element: <Search />,
  },
  // Declared before the lobby route: "/casino/:code" would otherwise match
  // "crash-original-game" and show a category that does not exist.
  ...ORIGINALS.map(({ path, name, Page }) => ({
    path,
    element: (
      <>
        <OriginalMeta name={name} />
        <Page />
      </>
    ),
  })),
  ...LEGACY_ORIGINALS.map(({ path, to }) => ({ path, element: <ToOriginal to={to} /> })),
  {
    path: routers.GamePage,
    element: <GamePage />,
  },
  {
    path: routers.Providers,
    element: <Providers />,
  },
  {
    path: routers.ProviderPage,
    element: <ProviderPage />,
  },
  {
    path: routers.CasinoGame,
    element: <Games />,
  },
];

export const GameRoomRoutes = [
  {
    path: routers.GameRoom,
    element: <GameRoom />,
  },
];
